import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { mathUtils } from "@/lib/utils";
import {
  WifiOff,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import Sidebar from "@/components/sidebar";
import MobileMenuButton from "@/components/ui/mobile-menu-button";
import PageHeader from "@/components/page-header.jsx";
import PageHeaderActions from "@/components/page-header-actions.jsx";
import FilterDrawer from "@/components/filter-drawer";
import CustomDateRangePicker from "@/components/custom-date-range-picker";
import { StatCard } from "@/components/downtime-stat-card";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardHeaderActions } from "@/components/ui/card-header-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingSkeleton } from "@/components/ui/loading";
import TablePagination from "@/components/ui/table-pagination";
import { useTableUrlState } from "@/hooks/useTableUrlState";
import { useFilters } from "@/hooks/useFilters";
import { buildAssetLinkSearch } from "@/lib/table-url-state";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext";
import { TIME_RANGE_OPTIONS } from "@/constants/chart-constants";
import { cn } from "@/lib/utils";
import { cardStyles, dateRangePillIdle, dateRangePillSelected, dateRangePillTrack } from "@/lib/status-styles";
import { downloadNoDataReportCSV } from "@/lib/csv-utils";
import {
  getCutoffsForRange,
  filterSilentAssets,
  bucketSilentAssets,
  sortNoDataAssets,
  formatCutoffDisplay,
  enrichAssetsWithNoDataStats,
  formatNoDataEventCount,
  formatAvailabilityPercent,
  getRangeSubtitle,
} from "@/lib/no-data-utils";
import { useNoDataStatsRange } from "@/hooks/useNoDataStatsRange.js";
import { filterAssetsBySearch } from "@/lib/asset-data-utils";
import {
  formatTimestamp,
  formatTimestampForSearch,
} from "@/lib/datetime-utils";

const RANGE_KEYS = ["24h", "7d", "30d", "custom"];

const TABLE_COLUMNS = [
  { key: "assetNumber", label: "Asset Number" },
  { key: "vendor", label: "Vendor" },
  { key: "location", label: "Location" },
  { key: "customer", label: "Customer" },
  { key: "telemetrySource", label: "Telemetry" },
  { key: "lastUpdated", label: "Last Updated" },
  { key: "noDataEventCount", label: "No Data Event Count" },
  { key: "availabilityPercent", label: "Communications Availability (%)" },
  { key: "silentDurationMs", label: "Silent For" },
  { key: "severity", label: "Severity" },
];

const NO_DATA_VALID_SORT_COLUMNS = new Set(TABLE_COLUMNS.map((c) => c.key));

function loadStored(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw != null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function persistStored(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function NoDataStatsCell({ isLoading, children, skeletonClassName = "h-4 w-14 rounded" }) {
  return isLoading ? (
    <LoadingSkeleton className={skeletonClassName} />
  ) : (
    children
  );
}

export default function NoDataReport() {
  const [, setLocation] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { filters, handleFilterChange, handleStatusClick, clearFilters } =
    useFilters();
  const { isLoading, error } = useAssets();

  const [searchTerm, setSearchTerm] = useState(() =>
    loadStored("noDataSearchTerm", "")
  );
  const [selectedRange, setSelectedRange] = useState(() =>
    loadStored("noDataRange", "24h")
  );
  const [previousRange, setPreviousRange] = useState("24h");
  const [customStartDate, setCustomStartDate] = useState(() =>
    loadStored("noDataCustomStart", "")
  );
  const [customEndDate, setCustomEndDate] = useState(() =>
    loadStored("noDataCustomEnd", "")
  );

  const {
    effectiveRange,
    entry: noDataStatsEntry,
    statsByAssetId,
  } = useNoDataStatsRange({
    selectedRange,
    customStart: customStartDate,
    customEnd: customEndDate,
    previousRange,
  });

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortColumn,
    sortDirection,
    handleSort,
    resetToFirstPage,
  } = useTableUrlState({
    basePath: "/no-data-report",
    validSortColumns: NO_DATA_VALID_SORT_COLUMNS,
    defaultSortColumn: "silentDurationMs",
    defaultSortDirection: "desc",
  });

  const { filteredAssets } = useFilteredAssets(filters, "");

  const now = useMemo(() => new Date(), [filteredAssets, isLoading]);

  const cutoffs = useMemo(
    () =>
      getCutoffsForRange(
        selectedRange,
        customStartDate,
        customEndDate,
        now
      ),
    [selectedRange, customStartDate, customEndDate, now]
  );

  const silentAssets = useMemo(
    () => filterSilentAssets(filteredAssets, cutoffs),
    [filteredAssets, cutoffs]
  );

  const searchFilteredSilent = useMemo(
    () =>
      filterAssetsBySearch(
        silentAssets,
        searchTerm,
        formatTimestampForSearch
      ),
    [silentAssets, searchTerm]
  );

  const statsEnrichedSilent = useMemo(
    () => enrichAssetsWithNoDataStats(searchFilteredSilent, statsByAssetId),
    [searchFilteredSilent, statsByAssetId]
  );

  const buckets = useMemo(
    () =>
      bucketSilentAssets(
        silentAssets,
        now,
        cutoffs.rangeLabel
      ),
    [silentAssets, now, cutoffs.rangeLabel]
  );

  const sortedRows = useMemo(
    () =>
      sortNoDataAssets(
        statsEnrichedSilent,
        sortColumn,
        sortDirection,
        now
      ),
    [statsEnrichedSilent, sortColumn, sortDirection, now]
  );

  const totalItems = sortedRows.length;
  const totalPages = mathUtils.ceil(totalItems / itemsPerPage);
  const adjustedCurrentPage =
    currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
  const startIndex = (adjustedCurrentPage - 1) * itemsPerPage;
  const endIndex = mathUtils.min(startIndex + itemsPerPage, totalItems);
  const paginatedItems = useMemo(
    () =>
      totalItems === 0 ? [] : sortedRows.slice(startIndex, endIndex),
    [sortedRows, startIndex, endIndex, totalItems]
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  useEffect(() => {
    resetToFirstPage();
  }, [
    searchTerm,
    selectedRange,
    customStartDate,
    customEndDate,
    filters,
    resetToFirstPage,
  ]);

  const fleetPctSilent =
    filteredAssets.length > 0
      ? (silentAssets.length / filteredAssets.length) * 100
      : 0;
  const rangeSubtitle = getRangeSubtitle(effectiveRange);

  useEffect(() => {
    persistStored("noDataSearchTerm", searchTerm);
  }, [searchTerm]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedRange("24h");
    setPreviousRange("24h");
    setCustomStartDate("");
    setCustomEndDate("");
    persistStored("noDataRange", "24h");
    persistStored("noDataCustomStart", "");
    persistStored("noDataCustomEnd", "");
    clearFilters();
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? (
        <ChevronUp className='h-4 w-4 ml-1 text-muted-foreground' />
      ) : (
        <ChevronDown className='h-4 w-4 ml-1 text-muted-foreground' />
      );
    }
    return (
      <ChevronsUpDown className='h-4 w-4 ml-1 text-muted-foreground opacity-50' />
    );
  };

  if (error) {
    return (
      <div className='flex h-screen bg-background relative'>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <WifiOff className='h-12 w-12 mx-auto text-destructive mb-4' />
            <p className='text-destructive mb-2'>Error loading fleet data</p>
            <p className='text-muted-foreground text-sm'>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-screen bg-background relative'>
      <MobileMenuButton
        isOpen={isMobileMenuOpen}
        onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <Sidebar
        key='persistent-sidebar'
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      <div className='flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-200 ease-in-out'>
        <PageHeader
          icon={WifiOff}
          title='No-Data Communications Report'
          subtitle='Assets that have not reported telemetry within the selected window. Use this to spot communications dropouts before they hide real downtime.'>
          <PageHeaderActions showLastUpdated lastUpdatedContext='assets' />
        </PageHeader>

        <div className={cardStyles.pageContent}>
          <div className='flex flex-col flex-1 min-h-full gap-6'>
            <div className='shrink-0'>
              <FilterDrawer
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearAllFilters}
                onStatusClick={handleStatusClick}
                searchTerm={searchTerm}
              />
            </div>

            <div className='shrink-0 space-y-3'>
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                <div
                  className={cn(
                    "flex min-w-0 flex-wrap items-center gap-y-2",
                    selectedRange === "custom"
                      ? "w-full"
                      : "justify-start gap-x-2"
                  )}>
                  <span className='text-sm text-muted-foreground shrink-0 mr-1'>
                    No comms in last:
                  </span>
                  {selectedRange === "custom" ? (
                    <CustomDateRangePicker
                      startDate={customStartDate}
                      endDate={customEndDate}
                      onApply={(start, end) => {
                        setCustomStartDate(start);
                        setCustomEndDate(end);
                        persistStored("noDataCustomStart", start);
                        persistStored("noDataCustomEnd", end);
                        persistStored("noDataRange", "custom");
                      }}
                      onClear={() => {
                        setSelectedRange(previousRange);
                        setCustomStartDate("");
                        setCustomEndDate("");
                        persistStored("noDataCustomStart", "");
                        persistStored("noDataCustomEnd", "");
                        persistStored("noDataRange", previousRange);
                      }}
                    />
                  ) : (
                    <div className={cn(dateRangePillTrack, "justify-start")}>
                      {TIME_RANGE_OPTIONS.map((rangeLabel, index) => {
                        const internalRange = RANGE_KEYS[index];
                        return (
                          <Button
                            key={internalRange}
                            variant='outline'
                            size='sm'
                            onClick={() => {
                              if (internalRange === "custom") {
                                setPreviousRange(selectedRange);
                              }
                              setSelectedRange(internalRange);
                              persistStored("noDataRange", internalRange);
                            }}
                            className={cn(
                              "rounded-full whitespace-nowrap shadow-none font-medium",
                              selectedRange === internalRange
                                ? dateRangePillSelected
                                : dateRangePillIdle
                            )}>
                            {rangeLabel}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {cutoffs.endCutoff && selectedRange !== "custom" && (
                  <p className='text-sm text-muted-foreground shrink-0'>
                    Cutoff: {formatCutoffDisplay(cutoffs.endCutoff)}
                  </p>
                )}
                {selectedRange === "custom"
                  && customStartDate
                  && customEndDate && (
                    <p className='text-sm text-muted-foreground shrink-0'>
                      Window: {customStartDate} – {customEndDate}
                    </p>
                  )}
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className='bg-card rounded-lg border border-border shadow-sm p-4'>
                      <LoadingSkeleton className='h-5 w-28 rounded mb-3' />
                      <LoadingSkeleton className='h-8 w-16 rounded mb-2' />
                      <LoadingSkeleton className='h-4 w-24 rounded' />
                    </div>
                  ))
                ) : (
                  <>
                    <StatCard
                      title='Total Silent'
                      value={String(silentAssets.length)}
                      subtitle={`of ${filteredAssets.length} assets`}
                      icon={WifiOff}
                      status='summary'
                    />
                    <StatCard
                      title='24h–7d Silent'
                      value={String(buckets.within24h7d)}
                      subtitle={rangeSubtitle}
                      status='alerted'
                    />
                    <StatCard
                      title='7d–30d Silent'
                      value={String(buckets.within7d30d)}
                      subtitle={rangeSubtitle}
                      status='danger'
                    />
                    <StatCard
                      title='30d+ Silent'
                      value={String(buckets.beyond30d)}
                      subtitle={rangeSubtitle}
                      status='offline'
                    />
                    <StatCard
                      title='Fleet % Silent'
                      value={`${fleetPctSilent.toFixed(1)}%`}
                      subtitle={rangeSubtitle}
                      status='nodata'
                    />
                  </>
                )}
              </div>
            </div>

            {/* Table — fills remaining height with a scrollable floor */}
            <div className={cardStyles.tableFillRegion}>
              {isLoading ? (
                <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 h-full'>
                  <CardHeader className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 border-b border-border'>
                    <LoadingSkeleton className='h-6 w-48 rounded' />
                    <LoadingSkeleton className='h-9 w-64 rounded' />
                  </CardHeader>
                  <CardContent
                    className={cn(
                      cardStyles.content,
                      "flex flex-col flex-1 min-h-0"
                    )}>
                    <div
                      className={cn(
                        cardStyles.tableContainer,
                        cardStyles.tableFillBody
                      )}>
                      <Table className='w-full'>
                        <TableHeader className={cardStyles.tableHeader}>
                          <TableRow className={cardStyles.tableRow}>
                            {TABLE_COLUMNS.map((col) => (
                              <TableHead
                                key={col.key}
                                className={cardStyles.tableHead}>
                                <LoadingSkeleton className='h-5 w-20 rounded' />
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[...Array(6)].map((_, rowIndex) => (
                            <TableRow
                              key={rowIndex}
                              className={cardStyles.bodyRow}>
                              {TABLE_COLUMNS.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className={cardStyles.tableCell}>
                                  <LoadingSkeleton className='h-4 w-16 rounded' />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 h-full'>
                  <CardHeaderActions
                    title={`Silent Assets — ${sortedRows.length} matching`}
                    actions={[
                      {
                        id: 1,
                        text: `Export CSV (${sortedRows.length})`,
                        executeAction: () =>
                          downloadNoDataReportCSV(sortedRows, { now }),
                        actionType: "Download",
                      },
                    ]}
                    searchEnabled
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder='Search unit, vendor, location, customer...'
                    searchTestId='input-search-no-data'
                    titleTestId='title-no-data-report'
                  />
                  <CardContent
                    className={cn(
                      cardStyles.content,
                      "flex flex-col flex-1 min-h-0"
                    )}>
                    <div
                      className={cn(
                        cardStyles.tableContainer,
                        cardStyles.tableFillBody
                      )}>
                      <Table className='w-full'>
                        <TableHeader className={cardStyles.tableHeader}>
                          <TableRow className={cardStyles.tableRow}>
                            {TABLE_COLUMNS.map((col) => (
                              <TableHead
                                key={col.key}
                                className={cardStyles.tableHead}
                                onClick={() => handleSort(col.key)}
                                data-testid={`header-${col.key}`}>
                                <div className='flex items-center'>
                                  {col.label}
                                  {getSortIcon(col.key)}
                                </div>
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedRows.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={TABLE_COLUMNS.length}
                                className='py-8 text-center text-muted-foreground font-light'>
                                No silent assets match the current filters and
                                window.
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedItems.map((row) => (
                              <TableRow
                                key={row.id ?? row.assetNumber}
                                className={cn(
                                  cardStyles.bodyRow,
                                  "cursor-pointer"
                                )}
                                onClick={() =>
                                  setLocation(
                                    `/asset/${encodeURIComponent(row.id)}/vitals${buildAssetLinkSearch(
                                      {
                                        sourceParam: "no-data-report",
                                        page: currentPage,
                                        itemsPerPage,
                                        sortColumn,
                                        sortDirection,
                                      }
                                    )}`
                                  )
                                }
                                data-testid={`row-no-data-${row.assetNumber}`}>
                                <TableCell
                                  className={cn(
                                    "text-sm text-foreground font-normal py-3 px-4 relative",
                                    row.optimizationEnabled && "pl-5"
                                  )}>
                                  {row.optimizationEnabled && (
                                    <div
                                      className='absolute left-0 top-0 bottom-0 w-[3px] bg-brand-cyan z-0'
                                      aria-hidden
                                    />
                                  )}
                                  <span className='relative z-0'>
                                    {row.assetNumber}
                                  </span>
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                                  {row.vendor || "—"}
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                                  {row.location || "—"}
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                                  {row.customer || row.lease || "—"}
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                                  {row.telemetrySource || "—"}
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4 whitespace-nowrap'>
                                  {formatTimestamp(row.lastUpdated)}
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4 whitespace-nowrap'>
                                  <NoDataStatsCell
                                    isLoading={noDataStatsEntry.isLoading}
                                    skeletonClassName='h-4 w-10 rounded'>
                                    {formatNoDataEventCount(
                                      row.noDataEventCount
                                    )}
                                  </NoDataStatsCell>
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4 whitespace-nowrap'>
                                  <NoDataStatsCell
                                    isLoading={noDataStatsEntry.isLoading}>
                                    {formatAvailabilityPercent(
                                      row.availabilityPercent
                                    )}
                                  </NoDataStatsCell>
                                </TableCell>
                                <TableCell className='text-sm text-foreground font-normal py-3 px-4 font-mono whitespace-nowrap'>
                                  {row.silentDurationLabel}
                                </TableCell>
                                <TableCell className='py-2 px-4'>
                                  <Badge
                                    variant='outline'
                                    className={cn(
                                      "font-light whitespace-nowrap",
                                      row.severity?.badgeClassName
                                    )}>
                                    {row.severity?.label}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    <div className='shrink-0'>
                      <TablePagination
                        currentPage={adjustedCurrentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        totalItems={totalItems}
                        startIndex={startIndex}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setItemsPerPage}
                        itemsLabel='silent assets'
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            <div className={cardStyles.pageBottomSpacer} aria-hidden='true' />
          </div>
        </div>
      </div>
    </div>
  );
}
